/**
 * Utility functions for CSV generation and download
 */

export interface CSVData {
    [key: string]: string | number | boolean | null | undefined;
}

/**
 * Converts an array of objects to CSV format
 */
export const convertToCSV = (data: CSVData[], filename?: string): string => {
    if (!data || data.length === 0) {
        return '';
    }

    // Get headers from the first object
    const headers = Object.keys(data[0]);
    
    // Create CSV header row
    const csvHeaders = headers.join(',');
    
    // Create CSV data rows
    const csvRows = data.map(row => {
        return headers.map(header => {
            const value = row[header];
            // Handle null/undefined values
            if (value === null || value === undefined) {
                return '';
            }
            // Escape commas and quotes in string values
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        }).join(',');
    });
    
    return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Downloads CSV data as a file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};

/**
 * Formats a date for CSV filename
 */
export const formatDateForFilename = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

/**
 * Creates a filename with timestamp
 */
export const createTimestampedFilename = (baseName: string, extension: string = 'csv'): string => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Creates a filename with date range (converted to Chilean timezone)
 */
export const createDateRangeFilename = (baseName: string, startDate: string, endDate: string, extension: string = 'csv'): string => {
    // Convert dates to Chilean timezone and format as YYYY-MM-DD
    const formatChileanDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-CA', {
            timeZone: 'America/Santiago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };
    
    const formattedStartDate = formatChileanDate(startDate);
    const formattedEndDate = formatChileanDate(endDate);
    return `${baseName}_${formattedStartDate}_to_${formattedEndDate}.${extension}`;
};
